import React, { useEffect, useState } from 'react';
import { Spin, message } from 'antd';
import { YingshiAccount, YingshiCamera, YingshiDevice } from './types';
import YingshiAccountSection from './YingshiAccount';
import YingshiDeviceList from './YingshiDeviceList';
import YingshiCameraList from './YingshiCameraList';
import { 
  getYingshiVideoTokenByUid, 
  getYingshiDevicesByAppKey, 
  getYingshiCamerasByDeviceSerial 
} from '../../services/api';

interface YingshiIntegrationProps {
  editingDevice: any | null;
  onCamerasSelected: (cameras: YingshiCamera[]) => void;
  form?: any; // Add form prop to allow setting form values
}

const YingshiIntegration: React.FC<YingshiIntegrationProps> = ({ 
  editingDevice,
  onCamerasSelected,
  form
}) => {
  // State for Yingshi Cloud integration
  const [yingshiAccount, setYingshiAccount] = useState<YingshiAccount | null>(null);
  const [yingshiDevices, setYingshiDevices] = useState<YingshiDevice[]>([]);
  const [selectedYingshiDevice, setSelectedYingshiDevice] = useState<YingshiDevice | null>(null);
  const [yingshiCameras, setYingshiCameras] = useState<YingshiCamera[]>([]);
  const [selectedCameras, setSelectedCameras] = useState<YingshiCamera[]>([]);
  const [yingshiLoading, setYingshiLoading] = useState(false);
  const [userUid, setUserUid] = useState<string>("");
  const [hasExistingChannels, setHasExistingChannels] = useState(false);
  const [existingDeviceSerial, setExistingDeviceSerial] = useState<string>("");
  const [availableAccounts, setAvailableAccounts] = useState<YingshiAccount[]>([]);

  // 新增一个函数，从 localStorage 获取当前用户 UID
  const getCurrentUserUid = (): string => {
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      try {
        const user = JSON.parse(userInfo);
        if (user && user.uid) {
          return user.uid.toString();
        }
      } catch (e) {
        console.error("Error parsing user info:", e);
      }
    }
    return "";
  };

  // 修改重置状态函数，确保重置后能加载当前用户
  const resetAllState = () => {
    setYingshiAccount(null);
    setYingshiDevices([]);
    setSelectedYingshiDevice(null);
    setYingshiCameras([]);
    setSelectedCameras([]);
    setHasExistingChannels(false);
    setExistingDeviceSerial("");
    
    // 清除表单中的萤石云相关字段
    if (form) {
      form.setFields([
        { name: 'appKey', value: undefined },
        { name: 'exData', value: {} }
      ]);
    }
  };

  // 在组件初始化时加载用户信息
  useEffect(() => {
    // 获取当前用户 UID
    const currentUid = getCurrentUserUid();
    if (currentUid && !userUid) {
      console.log('初始化时加载当前用户:', currentUid);
      setUserUid(currentUid);
    }
  }, []);

  // 修改主要的状态重置和数据加载逻辑
  useEffect(() => {
    // 先重置所有状态
    resetAllState();
    
    // 如果是编辑设备模式
    if (editingDevice) {
      const uid = editingDevice?.uid || getCurrentUserUid();
      
      if (uid) {
        setUserUid(uid);
        fetchYingshiAccount(uid);
        
        // 检查编辑的设备是否有已存在的通道
        if (editingDevice?.exData?.channel && 
            Array.isArray(editingDevice.exData.channel) && 
            editingDevice.exData.channel.length > 0) {
          
          setHasExistingChannels(true);
          
          // 存储第一个通道的设备序列号
          if (editingDevice.exData.channel[0].deviceSerial) {
            setExistingDeviceSerial(editingDevice.exData.channel[0].deviceSerial);
          }
          
          loadExistingChannels();
          
          // 如果表单可用且有 appKey，则设置 appKey
          if (form && editingDevice.exData.appKey) {
            form.setFieldValue('appKey', editingDevice.exData.appKey);
          }
        }
      }
    } 
    // 如果是新增设备模式
    else {
      // 获取当前用户 UID
      const currentUid = getCurrentUserUid();
      
      console.log('新增设备模式，获取当前用户:', currentUid);
      
      if (currentUid) {
        // 设置用户 UID 并获取账户信息
        setUserUid(currentUid);
        // 延迟执行，确保状态已重置
        setTimeout(() => {
          fetchYingshiAccount(currentUid);
        }, 100);
      }
    }
  }, [editingDevice]); // 只在 editingDevice 变化时触发

  // 观察 availableAccounts 变化，自动选择第一个账户
  useEffect(() => {
    // 当可用账户列表变化且处于新增设备模式时自动选择第一个账户
    if (!editingDevice && availableAccounts.length > 0 && !yingshiAccount) {
      autoSelectFirstAccount();
    }
  }, [availableAccounts, editingDevice, yingshiAccount]);

  // Update parent component when selected cameras change
  useEffect(() => {
    onCamerasSelected(selectedCameras);
  }, [selectedCameras, onCamerasSelected]);

  // Auto-select device when devices are loaded and we have an existing device serial
  useEffect(() => {
    if (existingDeviceSerial && yingshiDevices.length > 0 && !selectedYingshiDevice) {
      // Find the device that matches the existing device serial
      const matchingDevice = yingshiDevices.find(device => device.deviceSerial === existingDeviceSerial);
      
      if (matchingDevice) {
        // Auto-select the matching device
        setSelectedYingshiDevice(matchingDevice);
        message.info(`已自动选择设备: ${matchingDevice.deviceName}`);
        
        // Set form values if form is available
        if (form) {
          form.setFieldsValue({
            device_code: matchingDevice.deviceSerial,
            device_name: matchingDevice.deviceName,
            deviceCode: matchingDevice.deviceSerial,
            deviceName: matchingDevice.deviceName
          });
        }
        
        // Fetch cameras for this device
        if (yingshiAccount && yingshiAccount.appKey) {
          fetchYingshiCameras(matchingDevice.deviceSerial, yingshiAccount.appKey);
        }
      } else {
        // If no matching device found, show a message
        message.warning(`未找到设备序列号为 ${existingDeviceSerial} 的设备，请手动选择设备`);
      }
    }
  }, [yingshiDevices, existingDeviceSerial, selectedYingshiDevice, yingshiAccount, form]);

  // 添加一个新的 useEffect 用于初始化时加载所有可用账户
  useEffect(() => {
    // 这个函数只在组件挂载时执行一次
    const loadAvailableAccounts = async () => {
      try {
        // 先获取当前用户的 UID
        const currentUid = getCurrentUserUid();
        if (!currentUid) return;
        
        // 尝试加载当前用户的账户信息
        setYingshiLoading(true);
        const response = await getYingshiVideoTokenByUid(currentUid);
        
        if (response.data.code === 0 && response.data.data) {
          const accountData = response.data.data;
          // 只添加到可用账户列表，但不设置为当前账户
          setAvailableAccounts([accountData]);
        }
      } catch (error) {
        console.error("初始化加载账户错误:", error);
      } finally {
        setYingshiLoading(false);
      }
    };
    
    // 如果还没有可用账户，则加载
    if (availableAccounts.length === 0) {
      loadAvailableAccounts();
    }
  }, []); // 空依赖数组确保只执行一次

  // Fetch Yingshi account by UID
  const fetchYingshiAccount = async (uid: string) => {
    try {
      setYingshiLoading(true);
      const response = await getYingshiVideoTokenByUid(uid);
      
      if (response.data.code === 0 && response.data.data) {
        const accountData = response.data.data;
        setYingshiAccount(accountData);
        
        // 存储这个账户到可用账户列表
        setAvailableAccounts(prev => {
          const exists = prev.some(acc => acc.uid === accountData.uid);
          if (!exists) {
            return [...prev, accountData];
          }
          return prev;
        });
        
        // 设置 appKey 到表单
        if (form && accountData.appKey) {
          console.log('设置 appKey 到表单:', accountData.appKey);
          form.setFieldValue('appKey', accountData.appKey);
        }
        
        // 如果有 appKey，获取设备列表
        if (accountData.appKey) {
          fetchYingshiDevices(accountData.appKey);
        }
      } else {
        console.warn(`未找到 UID ${uid} 的萤石云账户`);
        
        // 如果当前UID的账户不存在但有其他账户，尝试使用第一个可用账户
        if (availableAccounts.length > 0 && !availableAccounts.some(acc => acc.uid === uid)) {
          const firstAccount = availableAccounts[0];
          console.log('自动切换到可用账户:', firstAccount.uid);
          setYingshiAccount(firstAccount);
          if (firstAccount.uid) {
            setUserUid(firstAccount.uid);
          }
          
          // 如果有 appKey，设置到表单并获取设备列表
          if (firstAccount.appKey) {
            if (form) {
              form.setFieldValue('appKey', firstAccount.appKey);
            }
            fetchYingshiDevices(firstAccount.appKey);
          }
        } else {
          setYingshiAccount(null);
        }
      }
    } catch (error) {
      console.error("获取萤石云账户错误:", error);
      setYingshiAccount(null);
      
      // 出错时也尝试使用可用账户
      if (availableAccounts.length > 0) {
        const firstAccount = availableAccounts[0];
        console.log('错误后切换到可用账户:', firstAccount.uid);
        setYingshiAccount(firstAccount);
        if (firstAccount.uid) {
          setUserUid(firstAccount.uid);
        }
        
        if (firstAccount.appKey) {
          if (form) {
            form.setFieldValue('appKey', firstAccount.appKey);
          }
          fetchYingshiDevices(firstAccount.appKey);
        }
      }
    } finally {
      setYingshiLoading(false);
    }
  };

  // Fetch Yingshi devices by appKey
  const fetchYingshiDevices = async (appKey: string) => {
    try {
      setYingshiLoading(true);
      const response = await getYingshiDevicesByAppKey(appKey);
      if (response.data.code === 0 && response.data.data) {
        setYingshiDevices(response.data.data);
      } else {
        console.error("获取萤石云设备列表失败:", response.data.msg);
        setYingshiDevices([]);
      }
    } catch (error) {
      console.error("获取萤石云设备列表错误:", error);
      setYingshiDevices([]);
    } finally {
      setYingshiLoading(false);
    }
  };

  // Load existing channels from editing device
  const loadExistingChannels = () => {
    if (!editingDevice?.exData?.channel || !Array.isArray(editingDevice.exData.channel) || editingDevice.exData.channel.length === 0) {
      return;
    }

    // Get existing channels from the device
    const existingChannels = editingDevice.exData.channel;
    
    // Mark these channels as pre-selected
    const selected = existingChannels.map((channel: any) => ({
      deviceSerial: channel.deviceSerial || "",
      ipcSerial: "",
      channelNo: channel.channelNo || 0,
      deviceName: "",
      localName: "",
      channelName: channel.channelName || `通道 ${channel.channelNo}`,
      status: channel.status || 0,
      isShared: "0",
      picUrl: "https://statics.ys7.com/device/assets/imgs/public/homeDevice.jpeg",
      isEncrypt: 0,
      videoLevel: 0,
      relatedIpc: true,
      isAdd: 1,
      devType: "",
      selected: true
    }));

    setSelectedCameras(selected);
  };

  // Fetch Yingshi cameras by deviceSerial and appKey
  const fetchYingshiCameras = async (deviceSerial: string, appKey: string) => {
    try {
      setYingshiLoading(true);
      const response = await getYingshiCamerasByDeviceSerial(deviceSerial, appKey);
      if (response.data.code === 0 && response.data.data) {
        // Get existing selected cameras if editing
        const existingSelectedSerials = editingDevice?.exData?.channel 
          ? editingDevice.exData.channel.map((c: any) => `${c.deviceSerial || ""}-${c.channelNo || 0}`)
          : [];

        // Add selected property for UI selection, mark as selected if matches existing
        const cameras = response.data.data.map((camera: YingshiCamera) => {
          const cameraKey = `${camera.deviceSerial}-${camera.channelNo}`;
          const isSelected = existingSelectedSerials.includes(cameraKey);
          return {
            ...camera,
            selected: isSelected
          };
        });
        
        setYingshiCameras(cameras);
        
        // Update selectedCameras list with the ones that are selected
        const selected = cameras.filter(c => c.selected);
        setSelectedCameras(selected);
      } else {
        console.error("获取萤石云通道列表失败:", response.data.msg);
        setYingshiCameras([]);
        
        // If editing and has existing channels, load them
        if (hasExistingChannels) {
          loadExistingChannels();
        }
      }
    } catch (error) {
      console.error("获取萤石云通道列表错误:", error);
      setYingshiCameras([]);
      
      // If editing and has existing channels, load them
      if (hasExistingChannels) {
        loadExistingChannels();
      }
    } finally {
      setYingshiLoading(false);
    }
  };

  // Handle selecting a Yingshi device
  const handleSelectYingshiDevice = (device: YingshiDevice) => {
    setSelectedYingshiDevice(device);
    
    // Set form values if form is available
    if (form) {
      form.setFieldsValue({
        device_code: device.deviceSerial,
        device_name: device.deviceName,
        deviceCode: device.deviceSerial,
        deviceName: device.deviceName
      });
    }
    
    if (yingshiAccount && yingshiAccount.appKey) {
      fetchYingshiCameras(device.deviceSerial, yingshiAccount.appKey);
    }
  };

  // Handle selecting/deselecting a camera
  const handleToggleCamera = (camera: YingshiCamera) => {
    const updatedCameras = yingshiCameras.map(c => {
      if (c.channelNo === camera.channelNo && c.deviceSerial === camera.deviceSerial) {
        return { ...c, selected: !c.selected };
      }
      return c;
    });
    
    setYingshiCameras(updatedCameras);
    
    // Update selectedCameras list
    const selected = updatedCameras.filter(c => c.selected);
    setSelectedCameras(selected);
  };

  // Handle switching to a different account
  const handleSwitchAccount = (uid: string) => {
    if (uid !== userUid) {
      setUserUid(uid);
      setSelectedYingshiDevice(null);
      setYingshiCameras([]);
      setSelectedCameras([]);
      fetchYingshiAccount(uid);
    }
  };

  // 新增自动选择第一个账户的函数
  const autoSelectFirstAccount = () => {
    if (availableAccounts.length > 0 && !yingshiAccount) {
      const firstAccount = availableAccounts[0];
      if (firstAccount && firstAccount.uid) {
        setUserUid(firstAccount.uid);
        fetchYingshiAccount(firstAccount.uid);
      }
    }
  };

  return (
    <div className="yingshi-integration">
      <Spin spinning={yingshiLoading && !yingshiAccount && !yingshiDevices && !yingshiCameras}>
        <YingshiAccountSection
          yingshiAccount={yingshiAccount}
          userUid={userUid}
          yingshiLoading={yingshiLoading && !yingshiAccount}
          setUserUid={setUserUid}
          fetchYingshiAccount={fetchYingshiAccount}
          availableAccounts={availableAccounts}
          onSwitchAccount={handleSwitchAccount}
        />

        {yingshiAccount && (
          <YingshiDeviceList
            yingshiDevices={yingshiDevices}
            selectedYingshiDevice={selectedYingshiDevice}
            yingshiLoading={yingshiLoading && !yingshiDevices}
            handleSelectYingshiDevice={handleSelectYingshiDevice}
          />
        )}

        <YingshiCameraList
          yingshiCameras={yingshiCameras}
          selectedCameras={selectedCameras}
          yingshiLoading={yingshiLoading && !yingshiCameras}
          selectedYingshiDevice={!!selectedYingshiDevice}
          editingExistingChannels={hasExistingChannels}
          handleToggleCamera={handleToggleCamera}
        />
      </Spin>
    </div>
  );
};

export default YingshiIntegration; 